import {
    ago,
    AsyncOperationId,
    AsyncOperationStatus,
    HomeCareLeadId,
    homeCarePartnerDescriptor,
    HomeCarePartnerIntegration,
    isAsyncOperationTerminal,
    UserId,
} from "@sp-crm/core";
import type { AppRouter } from "@sp-crm/server/rpc";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { inferProcedureOutput } from "@trpc/server";
import { AsyncOperationProgress } from "components/async-operation/async-operation-progress";
import {
    useAsyncOperation,
    UseAsyncOperationResult,
} from "components/async-operation/use-async-operation";
import { trpc } from "components/rpc/trpc";
import { fancyConfirm } from "components/ui/fancy-confirm";
import { InlineBanner } from "components/ui/inline-banner";
import { PanelType } from "components/ui/panel/panel-type";
import { ResponsivePanel } from "components/ui/panel/responsive-panel";
import { PrimaryButton } from "components/ui/primary-button";
import { SecondaryButton } from "components/ui/secondary-button";
import { Spinner } from "components/ui/spinner";
import React, { useCallback, useEffect, useState } from "react";
import { useQueryClient as useLegacyQueryClient } from "react-query";
import { useUsers } from "store/selectors/hooks";
import { z } from "zod";
import { ExternalRecordLink, ExternalRecordLinkCompact } from "./external-record-link";
import { SendReferralSource, SendSectionList } from "./send-preview";

const sendResultSchema = z.object({ url: z.string() });

type PartnerSend = inferProcedureOutput<
    AppRouter["homeCareLeadPartnerSend"]["sends"]
>[number];

interface SendToPartnerPanelProps {
    homeCareLeadId: HomeCareLeadId;
    integration: HomeCarePartnerIntegration | null;
    onDismiss: () => void;
}

export const SendToPartnerPanel: React.FC<SendToPartnerPanelProps> = ({
    homeCareLeadId,
    integration,
    onDismiss,
}) => {
    const [openedIntegration, setOpenedIntegration] =
        useState<HomeCarePartnerIntegration | null>(null);
    const [operationId, setOperationId] = useState<AsyncOperationId | null>(null);
    const trpcContext = trpc.useTRPC();
    const queryClient = useQueryClient();
    const legacyQueryClient = useLegacyQueryClient();

    useEffect(() => {
        if (integration === null || integration === openedIntegration) {
            return;
        }
        setOpenedIntegration(integration);
        setOperationId(null);
    }, [integration, openedIntegration]);

    const operation = useAsyncOperation(operationId);
    const handle = operation.handle;
    const partnerName = openedIntegration
        ? homeCarePartnerDescriptor(openedIntegration).partnerName
        : "";

    const invalidate = useCallback(() => {
        queryClient.invalidateQueries({
            queryKey: trpcContext.homeCareLeadPartnerSend.sends.queryKey(),
        });
        queryClient.invalidateQueries({
            queryKey: trpcContext.homeCareLead.get.queryOptions({ id: homeCareLeadId })
                .queryKey,
        });
        legacyQueryClient.invalidateQueries("getActivityFeed");
    }, [queryClient, legacyQueryClient, trpcContext, homeCareLeadId]);

    useEffect(() => {
        if (handle && isAsyncOperationTerminal(handle.status)) {
            invalidate();
        }
    }, [handle?.status]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (integration !== null || operation.isRunning) {
            return;
        }
        setOperationId(null);
    }, [integration, operation.isRunning]);

    const handleDismiss = useCallback(async () => {
        if (operation.isRunning) {
            const proceed = await fancyConfirm(
                "Still sending",
                `This send is still running and will finish in the background. Close this panel anyway?`,
                "Close",
                "Keep open",
            );
            if (!proceed) {
                return;
            }
        }
        onDismiss();
    }, [operation.isRunning, onDismiss]);

    return (
        <ResponsivePanel
            isOpen={integration !== null}
            onDismiss={handleDismiss}
            type={PanelType.medium}
            headerText={openedIntegration ? `Send to ${partnerName}` : "Send"}>
            {openedIntegration ? (
                <SendToPartnerBody
                    homeCareLeadId={homeCareLeadId}
                    integration={openedIntegration}
                    operation={operation}
                    operationId={operationId}
                    setOperationId={setOperationId}
                    onDismiss={onDismiss}
                />
            ) : null}
        </ResponsivePanel>
    );
};

const SenderName: React.FC<{ sentById: UserId }> = ({ sentById }) => {
    const userState = useUsers();
    const senderName = userState.users[sentById]?.name;
    return senderName ? <span className="text-gray-500"> by {senderName}</span> : null;
};

const PreviousSends: React.FC<{ sends: PartnerSend[] }> = ({ sends }) => (
    <ul className="divide-y divide-gray-100 border-y border-gray-100">
        {sends.map(send => (
            <li
                key={send.kind === "recorded" ? send.id : "legacy"}
                className="flex items-baseline justify-between gap-3 py-2 text-sm">
                {send.kind === "recorded" ? (
                    <span className="text-gray-900">
                        {ago(send.sentAt, "")}
                        <SenderName sentById={send.sentById} />
                    </span>
                ) : (
                    <span className="text-gray-900">Sent earlier</span>
                )}
                <ExternalRecordLinkCompact url={send.url} />
            </li>
        ))}
    </ul>
);

const PanelActions: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <div className="flex items-center justify-end gap-2">{children}</div>
);

interface SendToPartnerBodyProps {
    homeCareLeadId: HomeCareLeadId;
    integration: HomeCarePartnerIntegration;
    operation: UseAsyncOperationResult;
    operationId: AsyncOperationId | null;
    setOperationId: (operationId: AsyncOperationId | null) => void;
    onDismiss: () => void;
}

const SendToPartnerBody: React.FC<SendToPartnerBodyProps> = ({
    homeCareLeadId,
    integration,
    operation,
    operationId,
    setOperationId,
    onDismiss,
}) => {
    const { partnerName, recordNoun } = homeCarePartnerDescriptor(integration);
    const trpcContext = trpc.useTRPC();

    const [startError, setStartError] = useState<string | null>(null);

    const sendsQuery = useQuery(
        trpcContext.homeCareLeadPartnerSend.sends.queryOptions({
            homeCareLeadId,
            integration,
        }),
    );

    const previousSends = sendsQuery.data ?? [];
    const alreadySent = previousSends.length > 0;

    const previewQuery = useQuery({
        ...trpcContext.homeCareLeadPartnerSend.preview.queryOptions({ homeCareLeadId }),
        enabled: sendsQuery.isSuccess && !alreadySent,
    });

    const startMutation = useMutation({
        ...trpcContext.homeCareLeadPartnerSend.start.mutationOptions(),
        onSuccess: result => {
            setStartError(null);
            setOperationId(result.operationId);
        },
        onError: error => {
            setStartError(
                error instanceof Error
                    ? error.message
                    : `Unable to start the send to ${partnerName}.`,
            );
        },
    });

    const handle = operation.handle;
    const succeeded = operation.isComplete;

    const handleSend = useCallback(() => {
        startMutation.mutate({ homeCareLeadId, integration });
    }, [startMutation, homeCareLeadId, integration]);

    const handleSendAgain = useCallback(async () => {
        const proceed = await fancyConfirm(
            `Send to ${partnerName} again?`,
            `This creates a new ${recordNoun} in ${partnerName}. It does not update the ${recordNoun} that is already there.`,
            "Send again",
            "Cancel",
        );
        if (proceed) {
            startMutation.mutate({ homeCareLeadId, integration });
        }
    }, [startMutation, homeCareLeadId, integration, partnerName, recordNoun]);

    const handleBackFromFailure = useCallback(() => {
        setStartError(handle?.errorMessage ?? null);
        setOperationId(null);
    }, [handle, setOperationId]);

    if (operationId !== null && !succeeded) {
        return (
            <div className="space-y-3">
                {handle ? (
                    <AsyncOperationProgress
                        handle={handle}
                        percentComplete={operation.percentComplete}
                        pendingLabel={`Sending to ${partnerName}...`}
                        cancelLabel="Cancel"
                        cancelingLabel="Canceling..."
                        onCancel={operation.cancel}
                        isCanceling={operation.isCanceling}
                    />
                ) : (
                    <div className="flex items-center space-x-2">
                        <Spinner />
                        <span className="text-sm text-gray-700">
                            Sending to {partnerName}...
                        </span>
                    </div>
                )}
                {handle && isAsyncOperationTerminal(handle.status) ? (
                    <>
                        <p className="text-sm text-gray-600">
                            {handle.status === AsyncOperationStatus.Canceled
                                ? `Nothing was created in ${partnerName}.`
                                : `Nothing was recorded here. Check ${partnerName} for a partial ${recordNoun} before trying again.`}
                        </p>
                        <PanelActions>
                            <SecondaryButton onClick={handleBackFromFailure}>
                                Try again
                            </SecondaryButton>
                        </PanelActions>
                    </>
                ) : null}
            </div>
        );
    }

    if (succeeded && handle) {
        const parsed = sendResultSchema.safeParse(handle.result);
        return (
            <div className="space-y-3">
                <InlineBanner type="success">Sent to {partnerName}</InlineBanner>
                {parsed.success ? (
                    <ExternalRecordLink url={parsed.data.url} partnerName={partnerName} />
                ) : (
                    <InlineBanner type="warning">
                        Sent, but the link to the new {recordNoun} could not be read. Find
                        it in {partnerName}.
                    </InlineBanner>
                )}
                <PanelActions>
                    <SecondaryButton onClick={onDismiss}>Close</SecondaryButton>
                </PanelActions>
            </div>
        );
    }

    if (sendsQuery.isPending) {
        return (
            <div className="flex items-center space-x-2">
                <Spinner />
                <span className="text-sm text-gray-700">Loading details...</span>
            </div>
        );
    }

    if (sendsQuery.isError) {
        return (
            <InlineBanner type="error">
                We could not check whether this client was already sent to {partnerName}.
                Close the panel and try again.
            </InlineBanner>
        );
    }

    if (alreadySent) {
        return (
            <div className="space-y-3">
                <p className="text-sm text-gray-700">
                    This client has already been sent to {partnerName}.
                </p>
                <PreviousSends sends={previousSends} />
                {startError ? (
                    <InlineBanner type="error">{startError}</InlineBanner>
                ) : null}
                <PanelActions>
                    <SecondaryButton onClick={onDismiss}>Close</SecondaryButton>
                    <PrimaryButton
                        onClick={handleSendAgain}
                        disabled={startMutation.isPending}>
                        Send again
                    </PrimaryButton>
                </PanelActions>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {startError ? <InlineBanner type="error">{startError}</InlineBanner> : null}
            <p className="text-sm text-gray-700">
                These details will be sent to {partnerName}:
            </p>
            <SendPreview
                preview={previewQuery.data ?? null}
                isPending={previewQuery.isPending}
                isError={previewQuery.isError}
                partnerName={partnerName}
            />
            <PanelActions>
                <PrimaryButton
                    onClick={handleSend}
                    disabled={
                        previewQuery.isPending ||
                        previewQuery.isError ||
                        startMutation.isPending
                    }>
                    Send to {partnerName}
                </PrimaryButton>
            </PanelActions>
        </div>
    );
};

type Preview = inferProcedureOutput<AppRouter["homeCareLeadPartnerSend"]["preview"]>;

const SendPreview: React.FC<{
    preview: Preview | null;
    isPending: boolean;
    isError: boolean;
    partnerName: string;
}> = ({ preview, isPending, isError, partnerName }) => {
    if (isPending) {
        return (
            <div className="flex items-center space-x-2">
                <Spinner />
                <span className="text-sm text-gray-700">Loading details...</span>
            </div>
        );
    }
    if (isError || preview === null) {
        return (
            <InlineBanner type="error">
                We could not load the details for this client, so there is nothing to send
                to {partnerName} yet. Close the panel and try again.
            </InlineBanner>
        );
    }
    return (
        <div className="space-y-4">
            <SendSectionList sections={preview.sections} />
            <SendReferralSource
                referralSource={preview.referralSource}
                partnerName={partnerName}
            />
        </div>
    );
};
